import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Stethoscope,
  Brain,
  Sparkles,
  Send,
  Check,
  MessageCircle,
  Star,
  Volume2,
  Wifi,
  Battery,
  Smartphone,
  Headphones,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

export default function RecommendationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [ptaTests, setPtaTests] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState("");
  const [selectedTest, setSelectedTest] = useState("");
  const [recommendations, setRecommendations] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Lifestyle questionnaire state
  const [lifestyle, setLifestyle] = useState({
    noise_exposure: "moderate",
    tv_calls_usage: "sometimes",
    work_type: "retired",
    cosmetic_preference: "discreet",
    dexterity: "good",
    tech_comfort: "moderate",
    recharge_preference: "rechargeable",
    phone_os: "android",
    tinnitus: false,
    budget_min: 50000,
    budget_max: 300000
  });
  
  const [clinicalFlags, setClinicalFlags] = useState({
    asymmetric_loss: false,
    sudden_loss: false,
    ear_discharge: false
  });

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchPTATests(selectedPatient);
    }
  }, [selectedPatient]);

  const fetchPatients = async () => {
    try {
      const response = await apiClient.get("/patients");
      setPatients(response.data);
    } catch (error) {
      console.error("Failed to fetch patients:", error);
    }
  };

  const fetchPTATests = async (patientId) => {
    try {
      const response = await apiClient.get(`/pta-tests?patient_id=${patientId}`);
      setPtaTests(response.data);
      if (response.data.length > 0) {
        setSelectedTest(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch PTA tests:", error);
    }
  };

  const generateRecommendation = async () => {
    if (!selectedPatient || !selectedTest) {
      toast.error("Please select a patient and PTA test");
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        patient_id: selectedPatient,
        pta_test_id: selectedTest,
        lifestyle: lifestyle,
        clinical_flags: clinicalFlags
      };
      
      const response = await apiClient.post("/recommendations", payload);
      setRecommendations(response.data);
      setShowResults(true);
      toast.success("AI recommendations generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate recommendations");
    } finally {
      setGenerating(false);
    }
  };

  const sendViaWhatsApp = async () => {
    if (!recommendations) return;
    
    try {
      await apiClient.post("/whatsapp/send-recommendation", null, {
        params: { 
          patient_id: selectedPatient, 
          recommendation_id: recommendations.id 
        }
      });
      toast.success("Recommendation sent via WhatsApp! (Mocked)");
    } catch (error) {
      toast.error("Failed to send WhatsApp message");
    }
  };

  const selectedTestData = ptaTests.find(t => t.id === selectedTest);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="recommendations-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Brain className="w-7 h-7 text-violet-600" />
              AI Hearing Aid Recommender
            </h1>
            <p className="text-slate-500">Generate personalized hearing aid recommendations using AI</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Patient & Test Selection */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Patient Selection</CardTitle>
              <CardDescription>Choose patient and audiogram data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Patient *</Label>
                <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                  <SelectTrigger data-testid="rec-patient-select">
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.age} yrs)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>PTA Test *</Label>
                <Select value={selectedTest} onValueChange={setSelectedTest} disabled={!selectedPatient}>
                  <SelectTrigger data-testid="rec-test-select">
                    <SelectValue placeholder="Select PTA test" />
                  </SelectTrigger>
                  <SelectContent>
                    {ptaTests.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {new Date(t.created_at).toLocaleDateString()} - L: {t.pta_left?.toFixed(0)}dB / R: {t.pta_right?.toFixed(0)}dB
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Test Summary */}
              {selectedTestData && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <p className="font-medium text-sm">Audiogram Summary</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-red-50 rounded">
                      <p className="text-red-800 font-medium">Right Ear</p>
                      <p className="text-red-600">PTA: {selectedTestData.pta_right?.toFixed(1)} dB</p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {selectedTestData.severity_right || "N/A"}
                      </Badge>
                    </div>
                    <div className="p-2 bg-blue-50 rounded">
                      <p className="text-blue-800 font-medium">Left Ear</p>
                      <p className="text-blue-600">PTA: {selectedTestData.pta_left?.toFixed(1)} dB</p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {selectedTestData.severity_left || "N/A"}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}

              {/* Clinical Flags */}
              <div className="pt-4 border-t border-slate-100">
                <Label className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Clinical Flags
                </Label>
                <div className="space-y-2">
                  {[
                    { key: "asymmetric_loss", label: "Asymmetric hearing loss" },
                    { key: "sudden_loss", label: "Sudden onset loss" },
                    { key: "ear_discharge", label: "Ear discharge/infection" }
                  ].map(flag => (
                    <label key={flag.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={clinicalFlags[flag.key]}
                        onChange={(e) => setClinicalFlags({...clinicalFlags, [flag.key]: e.target.checked})}
                        className="rounded"
                      />
                      {flag.label}
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lifestyle Questionnaire */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Lifestyle Assessment</CardTitle>
              <CardDescription>Complete the questionnaire to personalize recommendations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Noise Exposure */}
                <div>
                  <Label>Noise Exposure Environment</Label>
                  <Select value={lifestyle.noise_exposure} onValueChange={(v) => setLifestyle({...lifestyle, noise_exposure: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - Mostly quiet environments</SelectItem>
                      <SelectItem value="moderate">Moderate - Some noisy situations</SelectItem>
                      <SelectItem value="high">High - Frequently in noise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Work Type */}
                <div>
                  <Label>Work / Lifestyle</Label>
                  <Select value={lifestyle.work_type} onValueChange={(v) => setLifestyle({...lifestyle, work_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="office">Office work</SelectItem>
                      <SelectItem value="outdoor">Outdoor / Active</SelectItem>
                      <SelectItem value="remote">Work from home</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Cosmetic Preference */}
                <div>
                  <Label>Cosmetic Preference</Label>
                  <Select value={lifestyle.cosmetic_preference} onValueChange={(v) => setLifestyle({...lifestyle, cosmetic_preference: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discreet">Discreet / Invisible preferred</SelectItem>
                      <SelectItem value="visible_ok">Visible is acceptable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Dexterity */}
                <div>
                  <Label>Manual Dexterity</Label>
                  <Select value={lifestyle.dexterity} onValueChange={(v) => setLifestyle({...lifestyle, dexterity: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="good">Good - No limitations</SelectItem>
                      <SelectItem value="limited">Limited - Need easy handling</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tech Comfort */}
                <div>
                  <Label>Technology Comfort</Label>
                  <Select value={lifestyle.tech_comfort} onValueChange={(v) => setLifestyle({...lifestyle, tech_comfort: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - Prefer simple devices</SelectItem>
                      <SelectItem value="moderate">Moderate - Can use apps</SelectItem>
                      <SelectItem value="high">High - Tech savvy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Phone OS */}
                <div>
                  <Label>Smartphone</Label>
                  <Select value={lifestyle.phone_os} onValueChange={(v) => setLifestyle({...lifestyle, phone_os: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ios">iPhone (iOS)</SelectItem>
                      <SelectItem value="android">Android</SelectItem>
                      <SelectItem value="none">No smartphone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Recharge Preference */}
                <div>
                  <Label>Power Preference</Label>
                  <Select value={lifestyle.recharge_preference} onValueChange={(v) => setLifestyle({...lifestyle, recharge_preference: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rechargeable">Rechargeable</SelectItem>
                      <SelectItem value="battery">Disposable batteries</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tinnitus */}
                <div className="flex items-center gap-3 pt-6">
                  <input
                    type="checkbox"
                    id="tinnitus"
                    checked={lifestyle.tinnitus}
                    onChange={(e) => setLifestyle({...lifestyle, tinnitus: e.target.checked})}
                    className="rounded w-5 h-5"
                  />
                  <Label htmlFor="tinnitus" className="cursor-pointer">Has Tinnitus (ringing in ears)</Label>
                </div>
              </div>

              {/* Budget Range */}
              <div className="pt-4 border-t border-slate-100">
                <Label className="mb-4 block">Budget Range (₹)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Minimum</Label>
                    <Input
                      type="number"
                      value={lifestyle.budget_min}
                      onChange={(e) => setLifestyle({...lifestyle, budget_min: parseInt(e.target.value) || 0})}
                      step={10000}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Maximum</Label>
                    <Input
                      type="number"
                      value={lifestyle.budget_max}
                      onChange={(e) => setLifestyle({...lifestyle, budget_max: parseInt(e.target.value) || 500000})}
                      step={10000}
                    />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Range: ₹{lifestyle.budget_min.toLocaleString()} - ₹{lifestyle.budget_max.toLocaleString()}
                </p>
              </div>

              {/* Generate Button */}
              <Button 
                className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-lg"
                onClick={generateRecommendation}
                disabled={generating || !selectedPatient || !selectedTest}
                data-testid="generate-recommendation-btn"
              >
                {generating ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Analyzing with AI...
                  </span>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate AI Recommendations
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results Dialog */}
        <Dialog open={showResults} onOpenChange={setShowResults}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Sparkles className="w-6 h-6 text-violet-600" />
                AI Recommendations
              </DialogTitle>
              <DialogDescription>
                Top 3 hearing aids matched to patient profile
              </DialogDescription>
            </DialogHeader>

            {recommendations && (
              <div className="space-y-6 mt-4">
                {/* Confidence Score */}
                <div className="flex items-center gap-4 p-4 bg-violet-50 rounded-lg">
                  <div className="text-4xl font-bold text-violet-600">
                    {Math.round(recommendations.confidence_score * 100)}%
                  </div>
                  <div>
                    <p className="font-medium text-violet-900">Match Confidence</p>
                    <p className="text-sm text-violet-700">Based on audiogram and lifestyle analysis</p>
                  </div>
                </div>

                {/* Recommendations Grid */}
                <div className="grid gap-4">
                  {recommendations.recommendations.map((rec, idx) => (
                    <Card 
                      key={rec.hearing_aid_id} 
                      className={`recommendation-card ${idx === 0 ? "border-violet-500 border-2" : ""}`}
                      data-testid={`recommendation-card-${idx}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            {idx === 0 && (
                              <Badge className="bg-violet-600">
                                <Star className="w-3 h-3 mr-1" />
                                Best Match
                              </Badge>
                            )}
                            <span className="text-lg font-semibold">#{idx + 1}</span>
                          </div>
                          <div className="match-score">
                            {rec.match_percentage}% Match
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                          {/* Product Info */}
                          <div className="md:col-span-2">
                            <h3 className="text-xl font-bold text-slate-900">{rec.name}</h3>
                            <p className="text-slate-500">{rec.brand} • {rec.model}</p>
                            
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Badge variant="outline">{rec.form_factor}</Badge>
                              {rec.rechargeable && (
                                <Badge variant="outline" className="text-green-600 border-green-200">
                                  <Battery className="w-3 h-3 mr-1" />
                                  Rechargeable
                                </Badge>
                              )}
                              {rec.streaming && (
                                <Badge variant="outline" className="text-blue-600 border-blue-200">
                                  <Wifi className="w-3 h-3 mr-1" />
                                  Bluetooth
                                </Badge>
                              )}
                            </div>

                            {/* Reasons */}
                            <div className="mt-4 space-y-2">
                              {rec.reasons?.map((reason, i) => (
                                <div key={i} className="flex items-start gap-2 text-sm">
                                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-slate-600">{reason}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Price */}
                          <div className="text-right">
                            <p className="text-3xl font-bold text-slate-900">
                              ₹{rec.price?.toLocaleString()}
                            </p>
                            {rec.price_pair && (
                              <p className="text-sm text-slate-500">
                                Pair: ₹{rec.price_pair?.toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* AI Explanation */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Brain className="w-5 h-5 text-violet-600" />
                      AI Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 whitespace-pre-line">{recommendations.explanation}</p>
                  </CardContent>
                </Card>

                {/* Next Actions */}
                <Card className="bg-sky-50 border-sky-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Recommended Next Steps</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {recommendations.next_actions.map((action, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">
                            {idx + 1}
                          </span>
                          <span className="text-slate-700">{action}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowResults(false)}>
                    Close
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={sendViaWhatsApp}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send via WhatsApp
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
